/*
 *  Announcements Widget for Dashboard
 *  Displays system announcements with colour-coded severity.
 *
 *  Props:
 *  - announcements: array of { id, type, color, content }
 *  - isLoading: boolean
 *  - isAvailable: boolean (false if announcements API is not deployed)
 */

import React from 'react';
import { Icon } from 'semantic-ui-react';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

const iconStyle = { margin: 0, lineHeight: 1, height: '1em' };

function AnnouncementsWidget({ announcements = [], isLoading = false, isAvailable = true }) {
  const headerRow = (badge) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: '600', color: '#111827' }}>
          <Icon name="bullhorn" style={{ ...iconStyle, marginRight: '8px', color: '#2563eb' }} />
          Announcements
        </h3>
        {badge}
      </div>
    </div>
  );

  if (!isAvailable) {
    return (
      <div className="workspace-card" style={{ overflow: 'hidden' }}>
        <div className="workspace-card-accent" style={{ background: '#2563eb' }} />
        <div style={{ padding: '20px 24px' }}>
          {headerRow(null)}
          <div style={{ color: '#94a3b8', fontSize: '0.9em', textAlign: 'center', padding: '24px 0' }}>
            Announcements are not available.
          </div>
        </div>
      </div>
    );
  }

  const badge = announcements.length > 0 ? (
    <span
      className="workspace-status-badge"
      style={{ background: '#eff6ff', color: '#2563eb', fontSize: '0.75em' }}
    >
      {announcements.length} {announcements.length === 1 ? 'notice' : 'notices'}
    </span>
  ) : null;

  return (
    <div className="workspace-card" style={{ overflow: 'hidden' }}>
      <div className="workspace-card-accent" style={{ background: '#2563eb' }} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {headerRow(badge)}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {isLoading ? (
            <ProgressPlaceHolder segmentCount={1} />
          ) : announcements.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {announcements.map((item, index) => (
                <AnnouncementItem key={item.id || index} item={item} />
              ))}
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '0.9em', textAlign: 'center', padding: '24px 0' }}>
              No current announcements
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnnouncementItem({ item }) {
  const color = item.color || '#2563eb';

  return (
    <div style={{
      display: 'flex',
      borderRadius: '10px',
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      background: `${color}08`,
    }}>
      <div style={{ width: '4px', flexShrink: 0, background: color }} />
      <div style={{ padding: '12px 14px', flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.7em',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: color,
          marginBottom: '4px',
        }}>
          {(item.type || 'Info').replace(/\*/g, '')}
        </div>
        <div style={{ fontSize: '0.88em', lineHeight: '1.5', color: '#1e293b' }}>
          {item.content}
        </div>
      </div>
    </div>
  );
}

export default AnnouncementsWidget;
